import axiosInstance from "../axios/axios-instance";
import {
  CancellationPolicy,
  CancellationPolicyRequest,
  CountryPaymentGateway,
  CountryPaymentGatewayRequest,
  CountryPricingConfig,
  CountryPricingConfigRequest,
  ExchangeRate,
  PaymentGateway,
  PaymentGatewayRequest,
  RefundRequest,
  RefundRequestAction,
  RefundRequestStatus,
} from "../@type/billing.types";
import { ApiResponse } from "../@type/package.types";
import { pathAPI } from "../utils/pathApi";

export interface RefundRequestPage {
  success: boolean;
  data: RefundRequest[];
  total: number;
}

export const BillingService = {
  getCancellationPolicies: () => {
    return axiosInstance.get<ApiResponse<CancellationPolicy[]>>(
      pathAPI.billingCancellationPolicies
    );
  },

  upsertCancellationPolicy: (countryCode: string, data: CancellationPolicyRequest) => {
    return axiosInstance.put<ApiResponse<CancellationPolicy>>(
      `${pathAPI.billingCancellationPolicies}/${countryCode}`,
      data
    );
  },

  getRefundRequests: (statuses: RefundRequestStatus[], page: number, size: number) => {
    return axiosInstance.get<RefundRequestPage>(pathAPI.billingRefundRequests, {
      params: { status: statuses.join(","), page, size },
    });
  },

  completeRefundRequest: (id: number, data: RefundRequestAction) => {
    return axiosInstance.post<ApiResponse<RefundRequest>>(
      `${pathAPI.billingRefundRequests}/${id}/complete`,
      data
    );
  },

  rejectRefundRequest: (id: number, data: RefundRequestAction) => {
    return axiosInstance.post<ApiResponse<RefundRequest>>(
      `${pathAPI.billingRefundRequests}/${id}/reject`,
      data
    );
  },

  getCountryPricingConfigs: () => {
    return axiosInstance.get<ApiResponse<CountryPricingConfig[]>>(
      pathAPI.billingCountryPricingConfigs
    );
  },

  upsertCountryPricingConfig: (data: CountryPricingConfigRequest) => {
    return axiosInstance.put<ApiResponse<CountryPricingConfig>>(
      pathAPI.billingCountryPricingConfigs,
      data
    );
  },

  getExchangeRates: () => {
    return axiosInstance.get<ApiResponse<ExchangeRate[]>>(pathAPI.billingExchangeRates);
  },

  refreshExchangeRates: () => {
    return axiosInstance.post<ApiResponse<ExchangeRate[]>>(
      `${pathAPI.billingExchangeRates}/refresh`
    );
  },

  setManualExchangeRate: (currency: string, rate: number) => {
    return axiosInstance.put<ApiResponse<ExchangeRate>>(
      `${pathAPI.billingExchangeRates}/${currency}`,
      { rate }
    );
  },

  resetExchangeRate: (currency: string) => {
    return axiosInstance.post<ApiResponse<ExchangeRate>>(
      `${pathAPI.billingExchangeRates}/${currency}/reset`
    );
  },

  getPaymentGateways: () => {
    return axiosInstance.get<ApiResponse<PaymentGateway[]>>(
      pathAPI.billingPaymentGateways
    );
  },

  createPaymentGateway: (data: PaymentGatewayRequest) => {
    return axiosInstance.post<ApiResponse<PaymentGateway>>(
      pathAPI.billingPaymentGateways,
      data
    );
  },

  updatePaymentGateway: (id: number, data: PaymentGatewayRequest) => {
    return axiosInstance.put<ApiResponse<PaymentGateway>>(
      `${pathAPI.billingPaymentGateways}/${id}`,
      data
    );
  },

  getCountryPaymentGateways: () => {
    return axiosInstance.get<ApiResponse<CountryPaymentGateway[]>>(
      pathAPI.billingCountryPaymentGateways
    );
  },

  createCountryPaymentGateway: (data: CountryPaymentGatewayRequest) => {
    return axiosInstance.post<ApiResponse<CountryPaymentGateway>>(
      pathAPI.billingCountryPaymentGateways,
      data
    );
  },

  updateCountryPaymentGateway: (id: number, data: CountryPaymentGatewayRequest) => {
    return axiosInstance.put<ApiResponse<CountryPaymentGateway>>(
      `${pathAPI.billingCountryPaymentGateways}/${id}`,
      data
    );
  },
};
